import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getEmployeesByWarehouseId } from 'services/warehouseService';
import { formatFirstLetterCapitalized } from 'utils/formatLabel';

const EMPLOYEE_DETAIL_CACHE_KEY = 'employeeDetailCache';

function getCachedSelectedEmployee() {
  try {
    const raw = sessionStorage.getItem(EMPLOYEE_DETAIL_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function setCachedSelectedEmployee(employee) {
  try {
    if (!employee) {
      sessionStorage.removeItem(EMPLOYEE_DETAIL_CACHE_KEY);
      return;
    }
    sessionStorage.setItem(EMPLOYEE_DETAIL_CACHE_KEY, JSON.stringify(employee));
  } catch {
    // ignore storage write errors
  }
}

function extractEmployeeList(responseData) {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.data?.data)) return responseData.data.data;
  return [];
}

function extractPagination(responseData, fallback = {}) {
  const pagination = responseData?.pagination || responseData?.data?.pagination || responseData?.data?.data?.pagination || {};
  return {
    page: Number(pagination.page ?? fallback.page ?? 1),
    limit: Number(pagination.limit ?? fallback.limit ?? 10),
    total: Number(pagination.total ?? 0)
  };
}

function mapEmployeeRows(responseData) {
  const list = extractEmployeeList(responseData);
  return list.map((item) => ({
    ...item,
    id: item.id ?? '-',
    full_name: formatFirstLetterCapitalized(item.full_name),
    phone: item.phone ?? '-',
    person_type: formatFirstLetterCapitalized(item.person_type),
    role_type: formatFirstLetterCapitalized(item.role_type),
    warehouse_name: formatFirstLetterCapitalized(item.warehouse_name),
    manage_access:
      item.manage_access === true
        ? 'Yes'
        : item.manage_access === false
          ? 'No'
          : formatFirstLetterCapitalized(item.manage_access),
    Status: formatFirstLetterCapitalized(item.Status ?? item.status)
  }));
}

export const fetchEmployeeListing = createAsyncThunk(
  'employee/fetchListing',
  async ({ warehouseId, page = 1, limit = 10 }, { rejectWithValue }) => {
    const response = await getEmployeesByWarehouseId(warehouseId, { page, limit });
    if (!response.ok) {
      return rejectWithValue(response.data?.message || 'Failed to fetch employees');
    }
    return {
      warehouseId,
      rows: mapEmployeeRows(response.data),
      pagination: extractPagination(response.data, { page, limit })
    };
  }
);

const employeeSlice = createSlice({
  name: 'employee',
  initialState: {
    rows: [],
    selectedEmployee: getCachedSelectedEmployee(),
    warehouseId: null,
    status: 'idle',
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0
    }
  },
  reducers: {
    setSelectedEmployee(state, action) {
      state.selectedEmployee = action.payload || null;
      setCachedSelectedEmployee(state.selectedEmployee);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeeListing.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchEmployeeListing.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.rows = action.payload.rows;
        state.warehouseId = action.payload.warehouseId;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchEmployeeListing.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch employees';
      });
  }
});

export const { setSelectedEmployee } = employeeSlice.actions;
export default employeeSlice.reducer;
